/**
 * VulnerabilitiesPanel — bloque de vulnerabilidades para los dashboards.
 *
 * Se usa tal cual en Equipos (ciType="workstation") y sirve igual para
 * Servidores ("rack_element") y máquinas virtuales ("virt_vm"): todo lo que
 * cambia es el `ciType` con el que se filtra.
 *
 * Por qué agrupado por producto y no una lista de 2.600 CVEs: un equipo con
 * Chrome de hace un año genera miles de hallazgos del mismo producto. Lo útil
 * es «actualiza Chrome» (cierra 2.624), no abrir 2.624 fichas. El detalle se
 * abre en un panel al pulsar el producto, y ahí se puede **aceptar con motivo**
 * (el motivo es obligatorio: sin él, el backend devuelve 400).
 *
 * Reglas de la casa: iconos de @heroicons, cero emojis, colores solo con
 * variables del sistema de diseño y datos honestos (si no hay catálogo, se dice).
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ShieldExclamationIcon,
  ArrowPathIcon,
  FireIcon,
  XMarkIcon,
  CheckIcon,
  ChevronRightIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import api from '../services/api';
import './VulnerabilitiesPanel.css';

const SEVERITY_FALLBACK = {
  critical: 'vln.severity.critical',
  high: 'vln.severity.high',
  medium: 'vln.severity.medium',
  low: 'vln.severity.low',
  unknown: 'vln.severity.unknown',
};

const fmt = (value) => (value === null || value === undefined ? '—' : value);

export default function VulnerabilitiesPanel({ companyId, ciType = '' }) {
  const { t } = useTranslation();  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const [product, setProduct] = useState(null); // producto abierto
  const [cves, setCves] = useState([]);
  const [cvesLoading, setCvesLoading] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(
    async ({ silent = false } = {}) => {
      if (!companyId) {
        setLoading(false);
        return;
      }
      if (silent) setRefreshing(true);
      else setLoading(true);
      setError('');
      try {
        const payload = await api.getVulnerabilitiesOverview(companyId, { ci_type: ciType });
        setData(payload || null);
      } catch (err) {
        setError(err.message || t('vln.err.load'));
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [companyId, ciType, t]
  );

  useEffect(() => {
    load();
  }, [load]);

  const openProduct = useCallback(
    async (item) => {
      setProduct(item);
      setCves([]);
      setSelected(new Set());
      setReason('');
      setCvesLoading(true);
      setError('');
      try {
        const payload = await api.getVulnerabilities(companyId, { ci_type: ciType, q: item.name });
        const items = (payload && payload.items) || [];
        // El filtro `q` es de texto: se queda solo con el producto exacto.
        setCves(items.filter((row) => row.software_name === item.name));
      } catch (err) {
        setError(err.message || t('vln.err.cves'));
      } finally {
        setCvesLoading(false);
      }
    },
    [companyId, ciType, t]
  );

  const closeProduct = useCallback(() => {
    setProduct(null);
    setCves([]);
    setSelected(new Set());
    setReason('');
  }, []);

  const toggle = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const acceptSelected = async () => {
    if (!selected.size) return;
    if (!reason.trim()) {
      setError(t('vln.err.reason'));
      return;
    }
    setSaving(true);
    setError('');
    try {
      const ids = Array.from(selected);
      for (const id of ids) {
        // Secuencial a propósito: si una falla, se sabe cuál y no se machaca la BD.
        await api.updateVulnerability(id, { status: 'accepted', reason: reason.trim() }, companyId);
      }
      setNotice(t('vln.accepted', { count: ids.length }));
      closeProduct();
      load({ silent: true });
    } catch (err) {
      setError(err.message || t('vln.err.accept'));
    } finally {
      setSaving(false);
    }
  };

  const recalc = async () => {
    setRefreshing(true);
    setError('');
    try {
      const result = await api.recomputeVulnerabilities(
        ciType ? { ci_type: ciType } : {},
        companyId
      );
      setNotice(
        t('vln.recalcDone', {
          ci: result.ci || 0,
          created: result.created || 0,
          removed: result.removed || 0,
        })
      );
      load({ silent: true });
    } catch (err) {
      setError(err.message || t('vln.err.recalc'));
    } finally {
      setRefreshing(false);
    }
  };

  const bySeverity = (data && data.by_severity) || {};
  const products = useMemo(() => (data && data.products) || [], [data]);
  const topCi = useMemo(() => (data && data.top_ci) || [], [data]);
  const total = (data && data.total) || 0;

  if (!companyId) return null;

  if (loading) {
    return (
      <section className="vln">
        <div className="vln-skeleton" aria-hidden="true">
          {Array.from({ length: 5 }).map((_, index) => (
            <span key={index} />
          ))}
        </div>
      </section>
    );
  }

  return (
    <section className="vln">
      <header className="vln-head">
        <span className={`vln-icon ${total ? 'is-warn' : 'is-ok'}`}>
          <ShieldExclamationIcon />
        </span>
        <div className="vln-titles">
          <h2>{t('vln.title')}</h2>
          <p>{t('vln.subtitle')}</p>
        </div>
        <div className="vln-actions">
          <button type="button" className="vln-btn" onClick={recalc} disabled={refreshing}>
            <ArrowPathIcon className={refreshing ? 'is-spinning' : ''} />
            {t('vln.recalc')}
          </button>
          <button
            type="button"
            className="vln-btn"
            onClick={() => load({ silent: true })}
            disabled={refreshing}
          >
            {t('vln.refresh')}
          </button>
        </div>
      </header>

      {error && (
        <div className="vln-alert">
          <ExclamationTriangleIcon />
          <span>{error}</span>
        </div>
      )}
      {notice && !error && (
        <div className="vln-notice">
          <CheckIcon />
          <span>{notice}</span>
        </div>
      )}

      <div className="vln-kpis">
        <div className="vln-kpi">
          <span className="vln-kpi-label">{t('vln.kpi.open')}</span>
          <strong>{total}</strong>
          <span className="vln-kpi-hint">{t('vln.kpi.affected', { count: (data && data.affected_ci) || 0 })}</span>
        </div>
        <div className={`vln-kpi ${bySeverity.critical ? 'is-bad' : ''}`}>
          <span className="vln-kpi-label">{t('vln.kpi.critical')}</span>
          <strong>{fmt(bySeverity.critical || 0)}</strong>
          <span className="vln-kpi-hint">{t('vln.kpi.high', { count: bySeverity.high || 0 })}</span>
        </div>
        <div className={`vln-kpi ${(data && data.kev) ? 'is-bad' : ''}`}>
          <span className="vln-kpi-label">
            <FireIcon /> {t('vln.kpi.exploited')}
          </span>
          <strong>{(data && data.kev) || 0}</strong>
          <span className="vln-kpi-hint">{t('vln.kpi.kevHint')}</span>
        </div>
        <div className="vln-kpi">
          <span className="vln-kpi-label">{t('vln.kpi.products')}</span>
          <strong>{products.length}</strong>
          <span className="vln-kpi-hint">{t('vln.kpi.productsHint')}</span>
        </div>
      </div>

      {total === 0 ? (
        <div className="vln-empty">
          <ShieldExclamationIcon />
          <div>
            <strong>{t('vln.empty.title')}</strong>
            <p>{t('vln.empty.text')}</p>
          </div>
        </div>
      ) : (
        <div className="vln-cols">
          <div className="vln-col">
            <h3>{t('vln.byProduct')}</h3>
            <p className="vln-col-hint">{t('vln.byProductHint')}</p>
            <table className="vln-table">
              <thead>
                <tr>
                  <th>{t('vln.col.product')}</th>
                  <th className="is-num">{t('vln.col.cves')}</th>
                  <th className="is-num">{t('vln.col.critical')}</th>
                  <th className="is-num">{t('vln.col.kev')}</th>
                  <th className="is-num">{t('vln.col.ci')}</th>
                  <th aria-label="Ver" />
                </tr>
              </thead>
              <tbody>
                {products.map((item) => (
                  <tr key={item.name} onClick={() => openProduct(item)} className="is-clickable">
                    <td className="vln-name">{item.name}</td>
                    <td className="is-num">{item.cves}</td>
                    <td className="is-num">{item.critical}</td>
                    <td className="is-num">{item.kev ? <span className="vln-kev">{item.kev}</span> : '—'}</td>
                    <td className="is-num">{item.ci}</td>
                    <td className="vln-chevron"><ChevronRightIcon /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="vln-col">
            <h3>{t('vln.worst')}</h3>
            <p className="vln-col-hint">{t('vln.worstHint')}</p>
            <ul className="vln-ci-list">
              {topCi.map((item) => (
                <li key={`${item.ci_type}-${item.ci_id}`}>
                  <span className="vln-ci-label">{item.label}</span>
                  <span className="vln-ci-metrics">
                    {item.kev > 0 && <span className="vln-kev">{item.kev} KEV</span>}
                    <span>{t('vln.worstCritical', { count: item.critical })}</span>
                    <span className="vln-ci-total">{item.cves}</span>
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {product && (
        <div className="vln-modal" role="dialog" aria-modal="true" aria-label={`CVEs de ${product.name}`}>
          <div className="vln-modal-card">
            <header className="vln-modal-head">
              <div>
                <h3>{product.name}</h3>
                <p>
                  {t('vln.modal.cves', { count: product.cves })}
                  {cves.length < product.cves
                    ? ` · ${t('vln.modal.truncated', { shown: cves.length })}`
                    : ''}
                  {' · '}
                  {t('vln.modal.hint')}.
                </p>
              </div>
              <button type="button" className="vln-icon-btn" onClick={closeProduct} aria-label={t('vln.close')}>
                <XMarkIcon />
              </button>
            </header>

            <div className="vln-modal-body">
              {cvesLoading ? (
                <div className="vln-skeleton" aria-hidden="true">
                  {Array.from({ length: 6 }).map((_, index) => (
                    <span key={index} />
                  ))}
                </div>
              ) : (
                <table className="vln-table vln-table--cves">
                  <thead>
                    <tr>
                      <th aria-label="Seleccionar" />
                      <th>CVE</th>
                      <th>{t('vln.col.version')}</th>
                      <th>{t('vln.col.severity')}</th>
                      <th className="is-num">{t('vln.col.cvss')}</th>
                      <th aria-label="KEV" />
                    </tr>
                  </thead>
                  <tbody>
                    {cves.map((item) => (
                      <tr key={item.id} className={selected.has(item.id) ? 'is-selected' : ''}>
                        <td>
                          <input
                            type="checkbox"
                            checked={selected.has(item.id)}
                            onChange={() => toggle(item.id)}
                            aria-label={`Seleccionar ${item.cve_id}`}
                          />
                        </td>
                        <td className="vln-cve-id">{item.cve_id}</td>
                        <td>{item.software_version || '—'}</td>
                        <td>
                          <span className={`vln-pill is-${item.severity}`}>
                            {t(SEVERITY_FALLBACK[item.severity] || 'vln.severity.unknown')}
                          </span>
                        </td>
                        <td className="is-num">{item.cvss_score ?? '—'}</td>
                        <td>{item.kev ? <span className="vln-kev">KEV</span> : null}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            <footer className="vln-modal-foot">
              <div className="vln-reason">
                <label htmlFor="vln-reason">{t('vln.reason.label')}</label>
                <textarea
                  id="vln-reason"
                  value={reason}
                  onChange={(event) => setReason(event.target.value)}
                  placeholder={t('vln.reason.placeholder')}
                  rows={2}
                />
              </div>
              <div className="vln-modal-actions">
                <span className="vln-selected">{t('vln.selected', { count: selected.size })}</span>
                <button type="button" className="vln-btn" onClick={closeProduct} disabled={saving}>
                  {t('vln.cancel')}
                </button>
                <button
                  type="button"
                  className="vln-btn vln-btn--primary"
                  onClick={acceptSelected}
                  disabled={saving || !selected.size}
                >
                  {saving ? t('vln.saving') : t('vln.accept')}
                </button>
              </div>
            </footer>
          </div>
        </div>
      )}
    </section>
  );
}
